import { callFunction } from '../utils/apiClient';
import { countsAsRealUsage } from '../utils/analyticsGate';

/**
 * Client telemetry — a single `track(event, props)` into our OWN queryable engagement spine (`telemetry_events`),
 * powering the admin Engagement view. Complements Firebase Analytics (GA4): GA4 is external + not queryable
 * in-app; this keeps an admin-queryable copy. Events are BATCHED (one request per ~5s or 20 events), best-effort
 * and fire-and-forget — telemetry must NEVER block or break the app. Keep props PII-FREE (ids/labels/counts only).
 */
let queue: Array<{ event: string; props?: Record<string, unknown> }> = [];
let timer: ReturnType<typeof setTimeout> | null = null;

const BATCH_MAX = 20;
const FLUSH_MS = 5000;

async function flush(): Promise<void> {
  if (timer) { clearTimeout(timer); timer = null; }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  try {
    await callFunction('logTelemetry', { events });
  } catch {
    /* best-effort — a failed telemetry flush never surfaces to the user or retries aggressively */
  }
}

/** Record a product-usage event (screen view, CTA click, funnel step). Batched + non-blocking. */
export function track(event: string, props?: Record<string, unknown>): void {
  if (!event) return;
  // Never record automated traffic (prerender / E2E / any WebDriver session) — see utils/analyticsGate.
  // This guard sits FIRST so a synthetic event is not even queued, let alone flushed to the backend.
  if (!countsAsRealUsage()) return;
  queue.push({ event, props });
  if (queue.length >= BATCH_MAX) { void flush(); return; }
  if (!timer) timer = setTimeout(() => { void flush(); }, FLUSH_MS);
}

// Flush queued events when the tab is hidden/closed so they aren't lost on navigation away.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') void flush();
  });
}
